import React, { useState } from 'react';
import { upsertProfile } from '../api/onboarding';
import { useOnboarding } from './OnboardingContext';
import { logOnboardingError } from '../api/errorLogger';

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const activityLevels = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'active', label: 'Active' },
];

const fitnessGoals = [
  { value: 'lose_fat', label: 'Lose fat' },
  { value: 'maintain', label: 'Maintain' },
  { value: 'gain_muscle', label: 'Gain muscle' },
];

export function ProfileSetupStep({ stepId }) {
  const { authUser, profileDraft, setProfileDraft, nextStep, markStepComplete, setLoading, loading } =
    useOnboarding();
  const [error, setError] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weightLbs, setWeightLbs] = useState('');

  // Initialize imperial units from metric stored values
  React.useEffect(() => {
    if (profileDraft.height_cm) {
      const totalInches = profileDraft.height_cm / 2.54;
      setHeightFeet(Math.floor(totalInches / 12).toString());
      setHeightInches(Math.round(totalInches % 12).toString());
    }
    if (profileDraft.weight_kg) {
      setWeightLbs(Math.round(profileDraft.weight_kg * 2.20462).toString());
    }
  }, [profileDraft.height_cm, profileDraft.weight_kg]);

  // Update metric values when imperial values change
  const handleHeightFeetChange = (feet) => {
    setHeightFeet(feet);
    const totalInches = (parseInt(feet) || 0) * 12 + (parseInt(heightInches) || 0);
    const cm = Math.round(totalInches * 2.54);
    setProfileDraft({ height_cm: cm > 0 ? cm.toString() : '' });
  };

  const handleHeightInchesChange = (inches) => {
    setHeightInches(inches);
    const totalInches = (parseInt(heightFeet) || 0) * 12 + (parseInt(inches) || 0);
    const cm = Math.round(totalInches * 2.54);
    setProfileDraft({ height_cm: cm > 0 ? cm.toString() : '' });
  };

  const handleWeightChange = (lbs) => {
    setWeightLbs(lbs);
    const kg = Math.round((parseInt(lbs) || 0) / 2.20462);
    setProfileDraft({ weight_kg: kg > 0 ? kg.toString() : '' });
  };

  if (!authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <p className="text-slate-300">Please create an account before setting up your profile.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!profileDraft.gender || !profileDraft.date_of_birth || !profileDraft.height_cm || !profileDraft.weight_kg) {
      setError('Please complete all required fields.');
      return;
    }

    if (!authUser || !authUser.id) {
      setError('Authentication error. Please sign in again.');
      return;
    }

    setLoading(true);
    try {
      const updates = {
        display_name: profileDraft.full_name || authUser.email?.split('@')[0] || 'Athlete',
        email: authUser.email,
        gender: profileDraft.gender,
        date_of_birth: profileDraft.date_of_birth,
        height_cm: Number(profileDraft.height_cm) || null,
        weight_kg: Number(profileDraft.weight_kg) || null,
        activity_level: profileDraft.activity_level || null,
        fitness_goal: profileDraft.fitness_goal || null,
        feature_tier: profileDraft.feature_tier || 'beta',
      };

      const { data, error: upsertError } = await upsertProfile(authUser.id, updates);

      if (upsertError) {
        console.error('Profile upsert error:', upsertError);
        // Provide more helpful error messages
        if (upsertError.code === '23502') {
          setError('Database configuration error. Please contact support.');
        } else if (upsertError.message) {
          setError(upsertError.message);
        } else {
          setError('Failed to save profile. Please try again.');
        }
        return;
      }

      if (!data) {
        console.warn('Profile saved but no data returned');
      }

      markStepComplete(stepId);
      nextStep();
    } catch (err) {
      console.error('Profile submit error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      // Log unexpected errors (API errors are logged in onboarding.js)
      await logOnboardingError({
        error: err,
        action: 'profile_submit_unexpected',
        stepId,
        authUserId: authUser?.id,
        userEmail: authUser?.email,
        metadata: {
          hasProfileData: !!profileDraft,
          gender: profileDraft.gender,
          hasHeight: !!profileDraft.height_cm,
          hasWeight: !!profileDraft.weight_kg,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-slate-800 border border-slate-700 rounded-3xl shadow-xl p-10 space-y-6"
      >
        <div>
          <h2 className="text-2xl font-semibold text-white">Tell us about yourself</h2>
          <p className="text-sm text-slate-300 mt-2">
            We use your stats to personalize training recommendations and nutrition insights.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1" htmlFor="gender">
              Gender
            </label>
            <select
              id="gender"
              value={profileDraft.gender || ''}
              onChange={(event) => setProfileDraft({ ...profileDraft, gender: event.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="onboarding-gender"
              required
            >
              <option value="">Select gender</option>
              {genderOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1" htmlFor="dob">
              Date of Birth
            </label>
            <input
              id="dob"
              type="date"
              value={profileDraft.date_of_birth || ''}
              onChange={(event) => setProfileDraft({ ...profileDraft, date_of_birth: event.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="onboarding-dob"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Height
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  id="height-feet"
                  type="number"
                  min="3"
                  max="8"
                  value={heightFeet}
                  onChange={(event) => handleHeightFeetChange(event.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="5"
                  data-testid="onboarding-height-feet"
                  required
                />
                <label className="text-xs text-slate-400 mt-1 block">Feet</label>
              </div>
              <div>
                <input
                  id="height-inches"
                  type="number"
                  min="0"
                  max="11"
                  value={heightInches}
                  onChange={(event) => handleHeightInchesChange(event.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="8"
                  data-testid="onboarding-height-inches"
                  required
                />
                <label className="text-xs text-slate-400 mt-1 block">Inches</label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1" htmlFor="weight">
              Weight (lbs)
            </label>
            <input
              id="weight"
              type="number"
              min="50"
              max="500"
              value={weightLbs}
              onChange={(event) => handleWeightChange(event.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="150"
              data-testid="onboarding-weight"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1" htmlFor="activity-level">
              Activity Level
            </label>
            <select
              id="activity-level"
              value={profileDraft.activity_level || ''}
              onChange={(event) => setProfileDraft({ ...profileDraft, activity_level: event.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="onboarding-activity"
            >
              <option value="">Select level</option>
              {activityLevels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1" htmlFor="fitness-goal">
              Fitness Goal
            </label>
            <select
              id="fitness-goal"
              value={profileDraft.fitness_goal || ''}
              onChange={(event) => setProfileDraft({ ...profileDraft, fitness_goal: event.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 text-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="onboarding-goal"
            >
              <option value="">Select goal</option>
              {fitnessGoals.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="submit"
            data-testid="onboarding-profile-continue"
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-lg"
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
